import { get } from 'lodash';
import { createRouter, createWebHistory } from 'vue-router';

import { loadRecaptchaScript, removeRecaptchaScript } from '@/helpers/manageRecaptcha';

import {
    FEATURE_FACEBOOK_AD_ACCOUNT,
    FEATURE_INVENTORY,
    FEATURE_LEAD_DESTINATION,
    FEATURE_PINTEREST_AD_ACCOUNT,
    FEATURE_PLAYBOOK,
    FEATURE_SNAPCHAT_AD_ACCOUNT,
    FEATURE_SNAPCHAT_PUBLIC_PROFILE,
    FEATURE_TIKTOK_AD_ACCOUNT,
    FEATURE_TIKTOK_IDENTITY,
    INTERFACE_BLANK,
    INTERFACE_FULL,
    INTERFACE_MINIMAL,
    VERTICAL_AUTOMOTIVE,
} from './helpers/globals';
import store from './store';

const router = createRouter({
    history: createWebHistory(import.meta.env.BASE_URL),
    routes: [
        {
            path: '/dev/icons',
            name: 'icons-list',
            component: () => import('@/components/globals/IconsList.vue'),
            meta: {
                requiresAuth: true,
                interfaceType: INTERFACE_MINIMAL,
                roles: ['super_admin', 'admin'],
            },
        },
        {
            path: '/',
            name: 'home',
            redirect: () => {
                return store.state.user.isLoggedIn ? store.getters.userHomepage : { name: 'login' };
            },
        },
        {
            path: '/dashboard',
            name: 'dashboard',
            redirect: () => {
                return store.state.user.isLoggedIn ? store.getters.userHomepage : { name: 'login' };
            },
        },
        {
            path: '/login',
            name: 'login',
            component: () => import('@/components/auth/login.vue'),
            meta: {
                betaDisclaimer: false,
                requiresAuth: false,
                interfaceType: INTERFACE_FULL,
            },
            props: true,
        },
        {
            path: '/password_reset',
            name: 'password-reset',
            component: () => import('@/components/auth/PasswordReset.vue'),
            meta: {
                betaDisclaimer: false,
                requiresAuth: false,
                interfaceType: INTERFACE_FULL,
                useRecaptcha: true,
            },
            beforeEnter: (to, from, next) => {
                store.dispatch('logout');
                next();
            },
        },
        {
            path: '/forgot-password',
            name: 'forgot-password',
            component: () => import('@/components/auth/ForgotPassword.vue'),
            meta: {
                betaDisclaimer: false,
                requiresAuth: false,
                interfaceType: INTERFACE_FULL,
            },
        },
        {
            path: '/permission-denied',
            name: 'permission-denied',
            component: () => import('@/components/auth/PermissionDenied.vue'),
            meta: {
                betaDisclaimer: false,
                requiresAuth: false,
                interfaceType: INTERFACE_FULL,
            },
        },
        {
            path: '/twofa-auth',
            name: 'twofa-auth',
            component: () => import('@/components/auth/TwoFAAuth.vue'),
            meta: {
                betaDisclaimer: false,
                requiresAuth: false,
                interfaceType: INTERFACE_FULL,
            },
        },
        {
            path: '/twofa-challenge',
            name: 'twofa-challenge',
            component: () => import('@/components/auth/TwoFAChallenge.vue'),
            meta: {
                betaDisclaimer: false,
                requiresAuth: false,
                interfaceType: INTERFACE_FULL,
            },
        },
        {
            path: '/dealers/:dealer_id(\\d+)',
            redirect: { name: 'global-dashboard' },
        },
        {
            path: '/dealers/:dealer_id(\\d+)/dashboard',
            redirect: { name: 'global-dashboard' },
        },
        {
            path: '/accounts/:dealer_id(\\d+)/dashboard',
            name: 'global-dashboard',
            component: () => import('@/components/dealers/dashboards/global/Dashboard.vue'),
            meta: {
                betaDisclaimer: false,
                requiresAuth: true,
                interfaceType: INTERFACE_FULL,
                roles: ['super_admin', 'admin', 'performance_manager', 'onboarding', 'client', 'agency_dashboard'],
            },
        },
        {
            path: '/users/settings',
            name: 'user-settings',
            component: () => import('@/components/users/UserSettings.vue'),
            meta: {
                betaDisclaimer: false,
                requiresAuth: true,
                interfaceType: INTERFACE_FULL,
                roles: ['super_admin', 'admin', 'performance_manager', 'onboarding', 'client', 'agency_dashboard'],
            },
        },
        {
            path: '/dealers/:dealer_id(\\d+)/settings',
            redirect: { name: 'dealer' },
        },
        {
            path: '/accounts/:dealer_id(\\d+)/settings',
            name: 'dealer',
            component: () => import('@/components/dealers/settings/DealerSettings.vue'),
            meta: {
                betaDisclaimer: false,
                requiresAuth: true,
                interfaceType: INTERFACE_FULL,
                roles: ['super_admin', 'admin', 'performance_manager', 'onboarding', 'agency_dashboard'],
            },
        },
        {
            path: '/dealers/:dealer_id(\\d+)/channel-setup/:channel',
            redirect: { name: 'channel-setup-product-selection' },
        },
        {
            path: '/accounts/:dealer_id(\\d+)/channel-setup/:channel',
            name: 'channel-setup-product-selection',
            component: () => import('@/components/onboarding/channel-setup/ProductSelection.vue'),
            meta: {
                betaDisclaimer: false,
                requiresAuth: false,
                interfaceType: INTERFACE_MINIMAL,
                roles: ['super_admin', 'admin', 'agency_dashboard', 'performance_manager'],
            },
        },
        {
            path: '/gtm-setup/:container_id([A-Za-z0-9\\-]+)',
            name: 'gtm-setup',
            component: () => import('@/components/public/GoogleTagManagerSetup.vue'),
            meta: {
                betaDisclaimer: false,
                requiresAuth: false,
                interfaceType: INTERFACE_FULL,
            },
        },
        {
            path: '/event-flow/:uuid',
            name: 'event-flow-setup',
            component: () => import('@/components/public/EventFlowSetup.vue'),
            meta: {
                betaDisclaimer: false,
                requiresAuth: false,
                interfaceType: INTERFACE_FULL,
            },
        },
        {
            path: '/event-flow-plus/:uuid',
            name: 'event-flow-plus-setup',
            component: () => import('@/components/public/EventFlowPlusSetup.vue'),
            meta: {
                betaDisclaimer: false,
                requiresAuth: false,
                interfaceType: INTERFACE_FULL,
            },
        },
        {
            path: '/dealers/:dealer_id(\\d+)/dashboards/facebook',
            redirect: { name: 'facebook-dashboard' },
        },
        {
            path: '/dealers/:dealer_id(\\d+)/reports/facebook-advertising',
            redirect: { name: 'facebook-dashboard' },
        },
        {
            path: '/accounts/:dealer_id(\\d+)/reports/facebook-advertising',
            redirect: { name: 'facebook-dashboard' },
        },
        {
            path: '/accounts/:dealer_id(\\d+)/dashboards/facebook',
            name: 'facebook-dashboard',
            component: () => import('@/components/dealers/dashboards/facebook/Dashboard.vue'),
            meta: {
                betaDisclaimer: false,
                requiresAuth: true,
                interfaceType: INTERFACE_FULL,
                roles: ['super_admin', 'admin', 'performance_manager', 'onboarding', 'client', 'agency_dashboard'],
            },
        },
        {
            path: '/dealers/:dealer_id(\\d+)/onboarding/:onboarding_id(\\d+)',
            redirect: { name: 'dealer-onboarding' },
        },
        {
            path: '/accounts/:dealer_id(\\d+)/onboarding/:onboarding_id(\\d+)',
            name: 'dealer-onboarding',
            component: () => import('@/components/onboarding/workflow/index.vue'),
            props: (route) => ({
                onboardingId: parseInt(route.params.onboarding_id),
                stepKey: route.query.step || null,
            }),
            meta: {
                betaDisclaimer: false,
                requiresAuth: true,
                interfaceType: INTERFACE_MINIMAL,
                hideHelpWidget: true,
                roles: ['super_admin', 'admin', 'agency_dashboard', 'performance_manager'],
            },
        },
        {
            path: '/dealers/:dealer_id(\\d+)/tools/offline-events',
            redirect: { name: 'dealer-offline-events' },
        },
        {
            path: '/accounts/:dealer_id(\\d+)/tools/offline-events',
            name: 'dealer-offline-events',
            component: () => import('@/components/tools/OfflineEvents.vue'),
            meta: {
                requiresAuth: true,
                interfaceType: INTERFACE_FULL,
                roles: ['super_admin', 'admin', 'agency_dashboard', 'performance_manager', 'client'],
            },
        },
        {
            path: '/accounts/:dealer_id(\\d+)/tools/audiences/inventory-ignite',
            name: 'dealer-inventory-ignite-audiences',
            component: () => import('@/components/tools/facebook/InventoryIgniteAudiences.vue'),
            meta: {
                requiresAuth: true,
                interfaceType: INTERFACE_FULL,
                roles: ['super_admin', 'admin', 'agency_dashboard', 'performance_manager'],
                agencies: [
                    2, // DU
                ],
            },
        },
        {
            path: '/dealers/:dealer_id(\\d+)/dashboards/snapchat',
            redirect: { name: 'snapchat-dashboard' },
        },
        {
            path: '/accounts/:dealer_id(\\d+)/dashboards/snapchat',
            name: 'snapchat-dashboard',
            component: () => import('@/components/dealers/dashboards/snapchat/Dashboard.vue'),
            meta: {
                betaDisclaimer: false,
                requiresAuth: true,
                interfaceType: INTERFACE_FULL,
                roles: ['client', 'super_admin', 'admin', 'performance_manager', 'agency_dashboard'],
            },
        },
        {
            path: '/dealers/:dealer_id(\\d+)/dashboards/tiktok',
            redirect: { name: 'tiktok-dashboard' },
        },
        {
            path: '/accounts/:dealer_id(\\d+)/dashboards/tiktok',
            name: 'tiktok-dashboard',
            component: () => import('@/components/dealers/dashboards/tiktok/Dashboard.vue'),
            meta: {
                betaDisclaimer: false,
                requiresAuth: true,
                interfaceType: INTERFACE_FULL,
                roles: ['client', 'super_admin', 'admin', 'performance_manager', 'agency_dashboard'],
            },
        },
        {
            path: '/dealers/:dealer_id(\\d+)/dashboards/pinterest',
            redirect: { name: 'pinterest-dashboard' },
        },
        {
            path: '/accounts/:dealer_id(\\d+)/dashboards/pinterest',
            name: 'pinterest-dashboard',
            component: () => import('@/components/dealers/dashboards/pinterest/Dashboard.vue'),
            meta: {
                betaDisclaimer: false,
                requiresAuth: true,
                interfaceType: INTERFACE_FULL,
                roles: ['client', 'super_admin', 'admin', 'performance_manager', 'agency_dashboard'],
            },
        },
        {
            path: '/dealers/:dealer_id(\\d+)/leads',
            redirect: { name: 'leads-all' },
        },
        {
            path: '/accounts/:dealer_id(\\d+)/leads',
            name: 'leads-all',
            component: () => import('@/components/leads/Leads.vue'),
            meta: {
                betaDisclaimer: false,
                requiresAuth: true,
                interfaceType: INTERFACE_FULL,
                roles: ['super_admin', 'admin', 'performance_manager', 'agency_dashboard', 'client'],
                features: [FEATURE_LEAD_DESTINATION],
            },
        },
        {
            path: '/dealers/:dealer_id(\\d+)/inventory',
            redirect: { name: 'inventory' },
        },
        {
            path: '/accounts/:dealer_id(\\d+)/inventory',
            name: 'inventory',
            component: () => import('@/components/inventory-dashboard/InventoryDashboard.vue'),
            meta: {
                betaDisclaimer: false,
                requiresAuth: true,
                interfaceType: INTERFACE_FULL,
                roles: ['super_admin', 'admin', 'performance_manager', 'agency_dashboard', 'client'],
                verticals: [VERTICAL_AUTOMOTIVE],
            },
        },
        {
            path: '/dealers/:dealer_id(\\d+)/products/playbooks',
            redirect: { name: 'dealer-playbooks' },
        },
        {
            path: '/accounts/:dealer_id(\\d+)/products/playbooks',
            name: 'dealer-playbooks',
            component: () => import('@/components/playbooks/Playbooks.vue'),
            meta: {
                requiresAuth: true,
                interfaceType: INTERFACE_FULL,
                roles: ['super_admin', 'admin', 'performance_manager', 'agency_dashboard'],
                features: [FEATURE_PLAYBOOK],
            },
        },
        {
            path: '/dealers/:dealer_id(\\d+)/products/playbooks/manage',
            redirect: { name: 'dealer-playbooks-manage' },
        },
        {
            path: '/accounts/:dealer_id(\\d+)/products/playbooks/manage',
            name: 'dealer-playbooks-manage',
            component: () => import('@/components/playbooks/ManagePlaybooks.vue'),
            props: (route) => ({
                deployedPlay: route.query.deployed_play ? parseInt(route.query.deployed_play) : null,
            }),
            meta: {
                requiresAuth: true,
                interfaceType: INTERFACE_FULL,
                roles: ['super_admin', 'admin', 'performance_manager', 'agency_dashboard'],
                features: [FEATURE_PLAYBOOK],
            },
        },
        {
            path: '/ad-previews',
            name: 'ad-previews',
            component: () => import('@/components/public/AdPreviews.vue'),
            meta: {
                betaDisclaimer: false,
                requiresAuth: false,
                interfaceType: INTERFACE_FULL,
            },
        },
        {
            path: '/dealers/:dealer_id(\\d+)/products/playbooks/custom',
            redirect: { name: 'dealer-playbooks-custom' },
        },
        {
            path: '/accounts/:dealer_id(\\d+)/products/playbooks/custom',
            name: 'dealer-playbooks-custom',
            component: () => import('@/components/playbooks/CustomPlaybooks.vue'),
            meta: {
                requiresAuth: true,
                interfaceType: INTERFACE_FULL,
                roles: ['super_admin', 'admin', 'performance_manager', 'agency_dashboard'],
                agencies: [
                    125, // LotVantage
                    112, // ADS
                ],
                features: [FEATURE_PLAYBOOK],
            },
        },
        {
            path: '/ads-library',
            name: 'ads-library',
            component: () => import('@/components/ads-library/AdsLibrary.vue'),
            redirect: () => {
                return { name: 'ads-tab' }; // default tab
            },
            children: [
                {
                    path: '/ads-library/ads',
                    name: 'ads-tab',
                    component: () => import('@/components/ads-library/AdsLibraryTab.vue'),
                    meta: {
                        betaDisclaimer: false,
                        requiresAuth: true,
                        interfaceType: INTERFACE_FULL,
                        roles: ['super_admin', 'admin', 'agency_dashboard', 'performance_manager'],
                    },
                },
                {
                    path: '/ads-library/playbooks',
                    name: 'playbooks-tab',
                    component: () => import('./components/ads-library/PlaybooksTab.vue'),
                    meta: {
                        betaDisclaimer: false,
                        requiresAuth: true,
                        interfaceType: INTERFACE_FULL,
                        roles: ['super_admin', 'admin', 'agency_dashboard', 'performance_manager'],
                    },
                },
                {
                    path: '/ads-library/assets',
                    name: 'assets-tab',
                    component: () => import('./components/ads-library/AssetsTab.vue'),
                    meta: {
                        betaDisclaimer: false,
                        requiresAuth: true,
                        interfaceType: INTERFACE_FULL,
                        roles: ['super_admin', 'admin', 'agency_dashboard', 'performance_manager'],
                    },
                },
            ],
            meta: {
                requiresAuth: true,
                interfaceType: INTERFACE_FULL,
                roles: ['super_admin', 'admin', 'agency_dashboard', 'performance_manager'],
            },
        },
        {
            path: '/ads-library/ads/:ad_id(\\d+)',
            name: 'ads-library-preview',
            component: () => import('@/components/ads-library/AdsLibraryPreview.vue'),
            props: (route) => ({
                adId: parseInt(route.params.ad_id),
            }),
            meta: {
                requiresAuth: true,
                interfaceType: INTERFACE_FULL,
                roles: ['super_admin', 'admin', 'agency_dashboard', 'performance_manager'],
            },
        },
        {
            path: '/accounts/:dealer_id(\\d+)/ad-deployment/:ad_deployment_id(\\d+)',
            redirect: { name: 'ad-deployment' },
        },
        {
            path: '/accounts/:dealer_id(\\d+)/ad-launcher/:ad_deployment_id(\\d+)',
            name: 'ad-deployment',
            component: () => import('@/components/ad-deployment/AdDeployment.vue'),
            meta: {
                requiresAuth: false,
                interfaceType: INTERFACE_MINIMAL,
                roles: ['super_admin', 'admin', 'performance_manager', 'agency_dashboard', 'client'],
            },
        },
        {
            path: '/resellers',
            redirect: { name: 'resellers' },
        },
        {
            path: '/organizations',
            name: 'resellers',
            component: () => import('@/components/agencies/Agencies.vue'),
            meta: {
                betaDisclaimer: false,
                requiresAuth: true,
                interfaceType: INTERFACE_FULL,
                roles: ['super_admin', 'performance_manager', 'agency_dashboard'],
            },
        },
        {
            path: '/agencies/:id(\\d+)',
            redirect: { name: 'agency' },
        },
        {
            path: '/organizations/:id(\\d+)',
            name: 'agency',
            component: () => import('@/components/agencies/AgencyID.vue'),
            meta: {
                betaDisclaimer: false,
                requiresAuth: true,
                interfaceType: INTERFACE_FULL,
                roles: ['super_admin', 'performance_manager', 'agency_dashboard'],
            },
        },
        {
            path: '/agency/new',
            redirect: { name: 'agency-new' },
        },
        {
            path: '/organizations/new',
            name: 'agency-new',
            component: () => import('@/components/agencies/AgencyAdd.vue'),
            meta: {
                betaDisclaimer: false,
                requiresAuth: true,
                interfaceType: INTERFACE_FULL,
                roles: ['super_admin', 'performance_manager', 'agency_dashboard'],
            },
        },
        {
            path: '/accounts/:dealer_id(\\d+)/campaign-management',
            name: 'campaign-management',
            component: () => import('@/components/playbook-management/PlaybookManagement.vue'),
            meta: {
                requiresAuth: true,
                interfaceType: INTERFACE_FULL,
                roles: ['super_admin', 'admin', 'performance_manager', 'agency_dashboard'],
                features: [
                    FEATURE_FACEBOOK_AD_ACCOUNT,
                    FEATURE_SNAPCHAT_AD_ACCOUNT,
                    FEATURE_SNAPCHAT_PUBLIC_PROFILE,
                    FEATURE_TIKTOK_AD_ACCOUNT,
                    FEATURE_TIKTOK_IDENTITY,
                    FEATURE_PINTEREST_AD_ACCOUNT,
                ],
            },
        },
        {
            path: '/accounts/:dealer_id(\\d+)/ad-preview',
            redirect: {
                name: 'ad-preview',
                params: { dealer_id: null },
            },
        },
        {
            path: '/ad-preview',
            name: 'ad-preview',
            component: () => import('@/components/playbook-management/ad-preview/AdPreviewPage.vue'),
            meta: {
                requiresAuth: false,
                interfaceType: INTERFACE_MINIMAL,
            },
        },
        {
            path: '/ad-preview-generator',
            name: 'ad-preview-generator',
            component: () => import('@/components/playbook-management/ad-preview/AdPreviewGenerator.vue'),
            meta: {
                requiresAuth: false,
                interfaceType: INTERFACE_MINIMAL,
            },
        },
        {
            path: '/account-management/:tab',
            name: 'account-management',
            component: () => import('@/components/account-management/AccountManagement.vue'),
            props: (route) => ({
                tab: route.params.tab,
            }),
            meta: {
                betaDisclaimer: false,
                requiresAuth: true,
                interfaceType: INTERFACE_FULL,
                roles: ['super_admin', 'admin', 'performance_manager', 'agency_dashboard'],
            },
        },
        {
            path: '/users/new',
            name: 'users-new',
            component: () => import('@/components/users/UserNew.vue'),
            meta: {
                betaDisclaimer: false,
                requiresAuth: true,
                interfaceType: INTERFACE_FULL,
                roles: ['super_admin', 'admin', 'performance_manager', 'agency_dashboard'],
            },
        },
        {
            path: '/dealers/:dealer_id(\\d+)/reports/vehicle-performance',
            redirect: { name: 'reports-vehicle-performance' },
        },
        {
            path: '/accounts/:dealer_id(\\d+)/reports/vehicle-performance',
            name: 'reports-vehicle-performance',
            component: () => import('@/components/reports/VehiclePerformance.vue'),
            meta: {
                betaDisclaimer: false,
                requiresAuth: true,
                interfaceType: INTERFACE_FULL,
                roles: ['client', 'super_admin', 'admin', 'performance_manager', 'agency_dashboard'],
                features: [FEATURE_INVENTORY, FEATURE_FACEBOOK_AD_ACCOUNT],
                verticals: [VERTICAL_AUTOMOTIVE],
            },
        },
        {
            path: '/dealers/:dealer_id(\\d+)/inventory/:id',
            redirect: { name: 'inventory-id' },
        },
        {
            path: '/accounts/:dealer_id(\\d+)/inventory/:id',
            name: 'inventory-id',
            component: () => import('@/components/inventory/VehicleDetail.vue'),
            meta: {
                betaDisclaimer: false,
                requiresAuth: true,
                interfaceType: INTERFACE_FULL,
                roles: ['super_admin', 'admin', 'performance_manager', 'agency_dashboard', 'client'],
            },
            props: true,
        },
        {
            path: '/resellers/:agency_id(\\d+)/reports/facebook-offline-events',
            redirect: { name: 'resellers-reports-facebook-offline-events' },
        },
        {
            path: '/organizations/:agency_id/reports/facebook-offline-events',
            name: 'resellers-reports-facebook-offline-events',
            component: () => import('@/components/resellers/FacebookOfflineEvents.vue'),
            meta: {
                requiresAuth: true,
                interfaceType: INTERFACE_FULL,
                roles: ['super_admin', 'admin', 'performance_manager', 'agency_dashboard'],
            },
        },
        {
            path: '/account/new',
            redirect: { name: 'account-new' },
        },
        {
            path: '/onboard-dealer',
            redirect: { name: 'account-new' },
        },
        {
            path: '/onboard-dealer-legacy',
            redirect: { name: 'account-new' },
        },
        {
            path: '/dealer/new',
            redirect: { name: 'account-new' },
        },
        {
            path: '/accounts/new',
            name: 'account-new',
            component: () => import('@/components/onboarding/AccountAdd.vue'),
            meta: {
                betaDisclaimer: false,
                requiresAuth: true,
                interfaceType: INTERFACE_MINIMAL,
                roles: ['super_admin', 'admin', 'agency_dashboard', 'performance_manager'],
            },
        },
        {
            path: '/dealers/:dealer_id(\\d+)/channel-setup',
            redirect: { name: 'channel-setup' },
        },
        {
            path: '/accounts/:dealer_id(\\d+)/channel-setup',
            name: 'channel-setup',
            component: () => import('@/components/onboarding/channel-setup/Selection.vue'),
            meta: {
                betaDisclaimer: false,
                requiresAuth: false,
                interfaceType: INTERFACE_MINIMAL,
            },
        },
        {
            path: '/dealers/:dealer_id(\\d+)/onboarding-complete',
            redirect: { name: 'onboarding-complete' },
        },
        {
            path: '/accounts/:dealer_id(\\d+)/onboarding-complete',
            name: 'onboarding-complete',
            component: () => import('@/components/onboarding/OnboardingComplete.vue'),
            meta: {
                betaDisclaimer: false,
                requiresAuth: false,
                interfaceType: INTERFACE_MINIMAL,
            },
        },
        {
            path: '/account/onboarding-status',
            redirect: { name: 'account-onboarding-status' },
        },
        {
            path: '/accounts/onboarding-status',
            name: 'account-onboarding-status',
            component: () => import('@/components/onboarding/AccountOnboardingStatus.vue'),
            meta: {
                betaDisclaimer: false,
                requiresAuth: true,
                interfaceType: INTERFACE_FULL,
                roles: ['super_admin', 'admin', 'agency_dashboard', 'performance_manager'],
            },
        },
        {
            path: '/resellers/:agency_id(\\d+)/billing',
            redirect: { name: 'resellers-billing' },
        },
        {
            path: '/organizations/:agency_id/billing',
            name: 'resellers-billing',
            component: () => import('@/components/resellers/AgencyBilling.vue'),
            meta: {
                requiresAuth: true,
                interfaceType: INTERFACE_FULL,
                roles: ['super_admin', 'admin', 'performance_manager', 'agency_dashboard'],
            },
        },
        {
            path: '/resellers/:agency_id(\\d+)/reports/playbooks',
            redirect: { name: 'resellers-reports-playbooks' },
        },
        {
            path: '/organizations/:agency_id/reports/playbooks',
            name: 'resellers-reports-playbooks',
            component: () => import('@/components/reports/ResellerPlaybooks.vue'),
            meta: {
                betaDisclaimer: false,
                requiresAuth: true,
                interfaceType: INTERFACE_FULL,
                roles: ['super_admin', 'agency_dashboard', 'performance_manager', 'admin'],
                agencies: [
                    100, // Social Hub
                ],
            },
        },
        {
            path: '/tools/website-inventory-review',
            name: 'website-inventory-review',
            component: () => import('@/components/tools/WebsiteInventoryReview.vue'),
            meta: {
                requiresAuth: true,
                interfaceType: INTERFACE_FULL,
                roles: ['super_admin', 'admin', 'agency_dashboard', 'performance_manager'],
            },
        },
        {
            path: '/tools/pricing-calculator',
            name: 'pricing-calculator',
            component: () => import('@/components/tools/PricingCalculator.vue'),
            meta: {
                requiresAuth: true,
                interfaceType: INTERFACE_FULL,
                roles: ['super_admin', 'admin'],
                agencies: [
                    2, // Dealers United
                ],
            },
        },
        {
            path: '/agency-token-report',
            name: 'agency-token-report',
            component: () => import('@/components/reports/AgencyTokenReport/index.vue'),
            meta: {
                requiresAuth: true,
                interfaceType: INTERFACE_FULL,
                roles: ['super_admin', 'admin'],
            },
        },
        {
            path: '/resellers/settings',
            redirect: { name: 'reseller-settings' },
        },
        {
            path: '/organizations/settings',
            name: 'reseller-settings',
            component: () => import('@/components/resellers/settings/Settings.vue'),
            meta: {
                requiresAuth: true,
                interfaceType: INTERFACE_FULL,
                roles: ['super_admin', 'admin', 'agency_dashboard', 'performance_manager'],
            },
        },
        {
            path: '/resellers/invites',
            redirect: { name: 'resellers-invites' },
        },
        {
            path: '/organizations/invites',
            name: 'resellers-invites',
            component: () => import('@/components/resellers/onboarding/Invites.vue'),
            meta: {
                betaDisclaimer: false,
                requiresAuth: true,
                interfaceType: INTERFACE_FULL,
                roles: ['super_admin', 'admin'],
            },
        },
        {
            path: '/sign-up',
            name: 'reseller-sign-up',
            component: () => import('@/components/resellers/onboarding/SignUp.vue'),
            meta: {
                betaDisclaimer: false,
                requiresAuth: false,
                interfaceType: INTERFACE_MINIMAL,
                hideHelpWidget: true,
            },
        },
        {
            path: '/sessions/reports',
            name: 'sessions',
            component: () => import('./components/sessions-reports/Sessions.vue'),
            meta: {
                betaDisclaimer: false,
                requiresAuth: true,
                interfaceType: INTERFACE_FULL,
                requireSessionPermission: true,
                roles: ['super_admin', 'admin', 'performance_manager', 'agency_dashboard'],
            },
        },
        {
            path: '/resellers/:agency_id(\\d+)/reports/:platform/budgeting',
            redirect: { name: 'resellers-reports-channel-budgeting' },
        },
        {
            path: '/resellers/:agency_id(\\d+)/reports/facebook-budgeting',
            redirect: { name: 'resellers-reports-channel-budgeting' },
        },
        {
            path: '/organizations/:agency_id(\\d+)/reports/facebook-budgeting',
            redirect: { name: 'resellers-reports-channel-budgeting' },
        },
        {
            path: '/organizations/:agency_id/reports/:platform/budgeting',
            name: 'resellers-reports-channel-budgeting',
            component: () => import('@/components/resellers/channel-budgeting/ChannelBudgeting.vue'),
            props: (route) => ({
                platform: route.params.platform,
            }),
            meta: {
                requiresAuth: true,
                interfaceType: INTERFACE_FULL,
                roles: ['super_admin', 'admin', 'performance_manager', 'agency_dashboard'],
            },
        },
        {
            path: '/resellers/:agency_id(\\d+)/reports/facebook-advertising',
            redirect: { name: 'resellers-reports-facebook-advertising' },
        },
        {
            path: '/organizations/:agency_id/reports/facebook-advertising',
            name: 'resellers-reports-facebook-advertising',
            component: () => import('@/components/resellers/facebook-advertising/FacebookAdvertising.vue'),
            meta: {
                requiresAuth: true,
                interfaceType: INTERFACE_FULL,
                roles: ['super_admin', 'admin', 'performance_manager', 'agency_dashboard'],
            },
        },
        {
            path: '/resellers/:agency_id(\\d+)/reports/:platform/advertising',
            redirect: { name: 'resellers-reports-channel-advertising' },
        },
        {
            path: '/organizations/:agency_id/reports/:platform/advertising',
            name: 'resellers-reports-channel-advertising',
            component: () => import('@/components/resellers/channel-advertising/ChannelAdvertising.vue'),
            props: (route) => ({
                platform: route.params.platform,
            }),
            meta: {
                requiresAuth: true,
                interfaceType: INTERFACE_FULL,
                roles: ['super_admin', 'admin', 'performance_manager', 'agency_dashboard'],
            },
        },
        {
            path: '/agencies/:agency_id(\\d+)/communication-central',
            redirect: { name: 'communication-central' },
        },
        {
            path: '/organizations/:agency_id/communication-central',
            name: 'communication-central',
            component: () => import('@/components/globals/CommunicationCentral.vue'),
            meta: {
                betaDisclaimer: false,
                requiresAuth: true,
                interfaceType: INTERFACE_FULL,
                roles: ['super_admin', 'admin', 'performance_manager', 'agency_dashboard'],
            },
        },
        {
            path: '/get-sessions-access',
            name: 'get-session-access',
            component: () => import('@/components/sessions-reports/SessionsAccessControl.vue'),
            meta: {
                betaDisclaimer: false,
                requiresAuth: true,
                interfaceType: INTERFACE_FULL,
                verifyIfHasSessionAccess: true,
                roles: ['super_admin', 'admin', 'performance_manager', 'agency_dashboard'],
            },
        },
        {
            path: '/:catchAll(.*)',
            name: '404',
            component: () => import('@/components/PageNotFound.vue'),
            meta: {
                interfaceType: INTERFACE_FULL,
            },
        },
        {
            path: '/permission-denied',
            name: 'permission-denied',
            component: () => import('@/components/auth/PermissionDenied.vue'),
            meta: {
                betaDisclaimer: false,
                requiresAuth: false,
                interfaceType: INTERFACE_FULL,
            },
        },
        {
            path: '/organizations/:agency_id/privacy-policy',
            name: 'agency-privacy-policy',
            component: () => import('@/components/resellers/PrivacyPolicy.vue'),
            meta: {
                betaDisclaimer: false,
                requiresAuth: true,
                interfaceType: INTERFACE_BLANK,
                roles: ['super_admin', 'admin', 'performance_manager', 'agency_dashboard'],
            },
        },
        {
            path: '/organizations/:agency_id/terms',
            name: 'agency-terms',
            component: () => import('@/components/resellers/Terms.vue'),
            meta: {
                betaDisclaimer: false,
                requiresAuth: true,
                interfaceType: INTERFACE_BLANK,
                roles: ['super_admin', 'admin', 'performance_manager', 'agency_dashboard'],
            },
        },
        {
            path: '/report-builder/templates',
            name: 'report-templates',
            component: () => import('@/components/report-builder/ReportTemplates.vue'),
            meta: {
                requiresAuth: true,
                interfaceType: INTERFACE_FULL,
                roles: ['super_admin', 'admin', 'agency_dashboard', 'performance_manager'],
            },
        },
        {
            path: '/build-snap-ads',
            name: 'build-snap-ads',
            component: () => import('./components/dealers/BuildSnapInventory/index.vue'),
            meta: {
                betaDisclaimer: false,
                requiresAuth: true,
                interfaceType: INTERFACE_FULL,
                roles: ['super_admin', 'admin', 'performance_manager', 'agency_dashboard'],
            },
        },
        {
            path: '/extract-campaigns',
            name: 'extract-campaigns',
            component: () => import('@/components/dealers/ExtractCampaigns.vue'),
            meta: {
                betaDisclaimer: false,
                requiresAuth: true,
                interfaceType: INTERFACE_FULL,
                roles: ['super_admin', 'admin', 'performance_manager', 'agency_dashboard'],
            },
        },
        {
            path: '/report-builder/templates/:template_id(\\d+)',
            name: 'report-builder',
            component: () => import('@/components/report-builder/Builder.vue'),
            meta: {
                requiresAuth: true,
                interfaceType: INTERFACE_BLANK,
                hideHelpWidget: true,
                roles: ['super_admin', 'admin', 'agency_dashboard', 'performance_manager'],
            },
        },
        {
            path: '/report-builder/preview/:template_id(\\d+)',
            name: 'report-preview',
            component: () => import('@/components/report-builder/ReportPreview.vue'),
            meta: {
                requiresAuth: true,
                interfaceType: INTERFACE_BLANK,
                hideHelpWidget: true,
                roles: ['super_admin', 'admin', 'agency_dashboard', 'performance_manager'],
            },
        },
    ],
});

router.beforeEach((to, from, next) => {
    if (to.meta.useRecaptcha) {
        loadRecaptchaScript();
    } else {
        removeRecaptchaScript();
    }

    // Allow the dealer in the URL to set the global dealer
    const routerDealerId = get(to, 'params.dealer_id', null);
    const currentDealerId = get(store.state.dealer, 'currentDealerId', null);
    if (routerDealerId !== null && parseInt(routerDealerId) !== currentDealerId) {
        store.dispatch('updateDealerById', parseInt(routerDealerId));
    }

    // Allow the agency in the URL to set the global agency
    const routerAgencyId = get(to, 'params.agency_id', null);
    const currentAgencyId = get(store.state.agency, 'currentAgency.id', null);

    if (routerAgencyId !== null && parseInt(routerAgencyId) !== currentAgencyId) {
        store.dispatch('updateAgencyById', parseInt(routerAgencyId));
    }

    const meta = get(to, 'matched[0].meta', null) || {};
    // If our current route doesn't require auth continue
    if (!meta.requiresAuth) {
        return next();
    }
    // If auth is required but we're not logged in, redirect to login and maintain redirect path
    if (!store.state.user.isLoggedIn) {
        return next({ path: '/login?redirect=' + to.fullPath });
    }

    const hasAccessToSessions = get(store.state.agency, 'currentAgency.configuration.has_white_label_access', null);

    if (meta.requireSessionPermission && !hasAccessToSessions) {
        return next({ name: 'get-session-access' });
    }

    if (meta.verifyIfHasSessionAccess && hasAccessToSessions) {
        return next({ name: 'sessions' });
    }

    // If the user doesn't have permission to view the current page, redirect to permission denied screen
    const requiredRoles = meta.roles || [];
    if (store.state.user.user && !requiredRoles.some(store.getters.userHasRole)) {
        return next({ name: 'permission-denied' });
    }

    // Assume the user has permission and progress
    next();
});

export default router;
